import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { EmployeeDropdownResponse, EmployeeService } from '@core/employee/employee.service';
import { SecurityRoleDropdownResponse, SecurityRoleService } from '@core/security/security-role.service';
import { SharedModule } from '@shared/shared.module';

export interface AssigneeSelection {
	selectedPersonIds: number[];
	requireAll: boolean;
	roleId: number;
}

@Component({
	selector: 'pms-task-assignee-picker',
	templateUrl: './task-assignee-picker.component.html',
	styleUrls: ['./task-assignee-picker.component.scss'],
	standalone: true,
	imports: [CommonModule, FormsModule, SharedModule],
})
export class TaskAssigneePickerComponent implements OnInit {
	private employeeService = inject(EmployeeService);
	private securityRoleService = inject(SecurityRoleService);

	private _employees: EmployeeDropdownResponse[] = [];

	@Input() initialSelectedPersonIds: number[] = [];
	@Input() initialRequireAll = false;
	@Input() initialRoleId: number = null;

	@Output() selectionChanged = new EventEmitter<AssigneeSelection>();

	roles: SecurityRoleDropdownResponse[] = [];
	selectedRoleId: number = null;
	searchText = '';
	displayedEmployees: EmployeeDropdownResponse[] = [];
	selectedPersonIds = new Set<number>();
	requireAll = false;
	isLoading = true;

	ngOnInit() {
		this.selectedPersonIds = new Set(this.initialSelectedPersonIds);
		this.requireAll = this.initialRequireAll;
		this.selectedRoleId = this.initialRoleId;

		combineLatest([
			this.employeeService.findActiveEmployeesWithPersonIdForDropdown(),
			this.securityRoleService.findSecurityRolesForDropdown(),
		]).subscribe(([employees, roles]) => {
			this._employees = employees;
			this.roles = roles;
			this.applySearchFilter();
			this.isLoading = false;
		});
	}

	private applySearchFilter() {
		if (this.searchText?.trim()) {
			const search = this.searchText.trim().toLowerCase();
			this.displayedEmployees = this._employees.filter(employee =>
				employee.label.toLowerCase().includes(search),
			);
		} else {
			this.displayedEmployees = this._employees;
		}
	}

	onRoleFilterChange() {
		this.selectedPersonIds.clear();
		if (this.selectedRoleId) {
			this.employeeService.findActiveEmployeePersonIdsBySecurityRoleId(this.selectedRoleId)
				.subscribe(personIds => {
					personIds.forEach(personId => this.selectedPersonIds.add(personId));
					this.emitSelection();
				});
		} else {
			this.emitSelection();
		}
	}

	onSearchChange() {
		this.applySearchFilter();
	}

	clearSearch() {
		this.searchText = '';
		this.applySearchFilter();
	}

	isSelected(employee: EmployeeDropdownResponse): boolean {
		return this.selectedPersonIds.has(employee.value);
	}

	toggleEmployee(employee: EmployeeDropdownResponse) {
		if (this.selectedPersonIds.has(employee.value)) {
			this.selectedPersonIds.delete(employee.value);
		} else {
			this.selectedPersonIds.add(employee.value);
		}
		this.emitSelection();
	}

	get allVisibleSelected(): boolean {
		return this.displayedEmployees.length > 0
			&& this.displayedEmployees.every(employee => this.selectedPersonIds.has(employee.value));
	}

	toggleSelectAll() {
		if (this.allVisibleSelected) {
			this.displayedEmployees.forEach(employee => this.selectedPersonIds.delete(employee.value));
		} else {
			this.displayedEmployees.forEach(employee => this.selectedPersonIds.add(employee.value));
		}
		this.emitSelection();
	}

	onRequireAllChange() {
		this.emitSelection();
	}

	private emitSelection() {
		this.selectionChanged.emit({
			selectedPersonIds: Array.from(this.selectedPersonIds),
			requireAll: this.requireAll,
			roleId: this.selectedRoleId,
		});
	}
}
